import { Component, EventEmitter, Input, Output } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import type { HandoffRow, OrderStatus, ServiceModel } from '../../../../core/api/api.types';
import {
  ALLOWED_STATUS_TRANSITIONS,
  ORDER_STATUS_LABELS,
  STATUS_SERVICE_MODEL,
} from '../../../../core/api/api.types';
import { HandoffSummaryCardComponent } from '../../../../shared/handoff-summary-card/handoff-summary-card.component';
import type { LiveOrder } from '../../my-cases.types';

export interface StatusOption {
  value: OrderStatus;
  label: string;
}

/**
 * A status pick that has been made but not yet saved. `from` records the status
 * the pick was based on: if the order moves underneath us (socket update, a save
 * from another tab) the pending pick is stale and is dropped rather than applied
 * to a state the operator never looked at.
 */
interface PendingStatus {
  status: OrderStatus;
  from: OrderStatus;
}

@Component({
  selector: 'op-case-sidebar',
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    HandoffSummaryCardComponent,
  ],
  templateUrl: './case-sidebar.component.html',
  styleUrl: './case-sidebar.component.scss',
})
export class CaseSidebarComponent {
  @Input() handoff: HandoffRow | null = null;
  @Input() orders: LiveOrder[] = [];
  @Input() ordersLoading = false;
  @Input() ordersSaving: Record<string, boolean> = {};
  @Input() demoMode = false;
  @Input() returningToAi = false;
  @Input() orderStatusOptions: StatusOption[] = [];
  @Input() serviceModelOptions: Array<{ value: ServiceModel; label: string }> = [];

  @Output() createOrderClicked = new EventEmitter<void>();
  @Output() editOrder = new EventEmitter<LiveOrder>();
  @Output() showSummary = new EventEmitter<LiveOrder>();
  @Output() statusChange = new EventEmitter<{ order: LiveOrder; status: OrderStatus }>();
  @Output() returnToAiClicked = new EventEmitter<void>();

  private pending: Record<string, PendingStatus> = {};

  orderTitle(order: LiveOrder): string {
    return order.service_name || order.category || order.order_code;
  }

  isOrderSaving(orderId: string): boolean {
    return !!this.ordersSaving[orderId];
  }

  // ─── Pending status (confirm before save) ────────────────────────────────────

  /**
   * The unsaved pick for this order, or null. Stale picks (the order changed
   * underneath the operator) are discarded here so the confirm bar never offers
   * to apply a transition that was reasoned about from a different state.
   */
  pendingStatusFor(order: LiveOrder): OrderStatus | null {
    const entry = this.pending[order.id];
    // Read-only on purpose: this runs inside change detection, so a stale entry
    // is reported as "no pending pick" and left to be overwritten rather than
    // mutated mid-pass.
    if (!entry || entry.from !== order.status) return null;
    return entry.status;
  }

  /** What the select displays: the unsaved pick if there is one, else the truth. */
  selectedStatus(order: LiveOrder): OrderStatus {
    return this.pendingStatusFor(order) ?? (order.status as OrderStatus);
  }

  /**
   * Selecting no longer saves — it stages the pick and reveals the confirm bar.
   * Re-picking the current status clears the pick instead of staging a no-op.
   */
  onStatusChange(order: LiveOrder, status: OrderStatus): void {
    if (status === order.status) {
      delete this.pending[order.id];
      return;
    }
    this.pending[order.id] = { status, from: order.status as OrderStatus };
  }

  /**
   * The pick is deliberately NOT cleared here. On success the parent swaps in an
   * order whose status is the new one, which makes the entry stale and retires
   * the confirm bar on its own. On failure the order is unchanged, so the bar
   * stays up and the operator can retry without re-picking.
   */
  confirmStatusChange(order: LiveOrder): void {
    const status = this.pendingStatusFor(order);
    if (!status) return;
    this.statusChange.emit({ order, status });
  }

  cancelStatusChange(order: LiveOrder): void {
    delete this.pending[order.id];
  }

  statusLabel(status: OrderStatus): string {
    return (
      this.orderStatusOptions.find((o) => o.value === status)?.label ??
      ORDER_STATUS_LABELS[status] ??
      status
    );
  }

  // ─── Option partitioning ─────────────────────────────────────────────────────

  /**
   * Statuses reachable from the order's current state. The current status is
   * always included — it is the displayed value and must stay selectable.
   * Service-model-incompatible statuses are dropped entirely (they are not a
   * "wrong order" mistake, they are meaningless for this order).
   */
  allowedStatusOptions(order: LiveOrder): StatusOption[] {
    const current = order.status as OrderStatus;
    const reachable = ALLOWED_STATUS_TRANSITIONS[current] ?? [];
    return this.modelCompatibleOptions(order).filter(
      (opt) => opt.value === current || reachable.includes(opt.value),
    );
  }

  /**
   * Statuses valid for this service model but not reachable from the current
   * state — rendered disabled so an accidental pick is impossible, and grouped
   * under a header that says why.
   */
  blockedStatusOptions(order: LiveOrder): StatusOption[] {
    const current = order.status as OrderStatus;
    const reachable = ALLOWED_STATUS_TRANSITIONS[current] ?? [];
    return this.modelCompatibleOptions(order).filter(
      (opt) => opt.value !== current && !reachable.includes(opt.value),
    );
  }

  blockedGroupLabel(order: LiveOrder): string {
    return `Not allowed from “${this.statusLabel(order.status as OrderStatus)}”`;
  }

  private modelCompatibleOptions(order: LiveOrder): StatusOption[] {
    const model = order.service_model as ServiceModel | '';
    if (!model) return this.orderStatusOptions;
    return this.orderStatusOptions.filter((opt) => {
      const restriction = STATUS_SERVICE_MODEL[opt.value];
      if (!restriction) return true;
      if (opt.value === order.status) return true;
      return restriction === model;
    });
  }

  statusTone(status: string): 'blue' | 'orange' | 'red' | 'green' {
    switch (status as OrderStatus) {
      case 'finding_provider':
      case 'in_progress': return 'blue';
      case 'confirmed':
      case 'completed':
      case 'completed_externally':
      case 'referral_sent': return 'green';
      case 'closed':
      case 'cancelled': return 'red';
      default: return 'orange';
    }
  }

}